/**
 * The SEPA mandate form's labels, built once (COL-533).
 *
 * `SepaMandateForm` is a §19-compliant mandate form and takes all 38 of its
 * strings as props. V1 spelled the mapping out inline; V2 needs the exact
 * same object to mount the exact same form, and two hand-written copies of
 * a 38-key mapping would drift — a missing key on one dashboard is a blank
 * label on a legally binding authorisation.
 *
 * Takes the already-translated `tenant.dashboard` dictionary, so the caller
 * keeps control of the language.
 */

import type { SepaMandateLabels } from "@/components/dashboard/SepaMandateForm";
import type { getPaymentsLabels } from "@/lib/tenant-dashboard-copy";

/** The already-translated `tenant.dashboard` payments dictionary. */
type PaymentsLabels = ReturnType<typeof getPaymentsLabels>;

export function buildSepaMandateLabels(
  /*
    Typed as the real dictionary, not `Record<string, string>`. With the loose
    type a typo in a SOURCE key reads as `string` to the compiler and renders
    `undefined` at runtime — a blank label on a legally binding mandate, found
    only by a human looking at the form.
  */
  p: PaymentsLabels
): SepaMandateLabels {
  return {
    title: p.sepaFormTitle,
    creditorHeading: p.sepaCreditorHeading,
    creditorNameLabel: p.sepaCreditorNameLabel,
    creditorIdLabel: p.sepaCreditorIdLabel,
    mandateReferenceLabel: p.sepaMandateReferenceLabel,
    mandateReferencePending: p.sepaMandateReferencePending,
    authorizationHeading: p.sepaAuthorizationHeading,
    authorizationText: p.sepaAuthorizationText,
    refundNoticeHeading: p.sepaRefundNoticeHeading,
    refundNoticeText: p.sepaRefundNoticeText,
    customerDataHeading: p.sepaCustomerDataHeading,
    salutationLabel: p.sepaSalutation,
    salutationMr: p.sepaMr,
    salutationMrs: p.sepaMrs,
    salutationDivers: p.sepaDivers,
    firstName: p.sepaFirstName,
    lastName: p.sepaLastName,
    street: p.sepaStreet,
    houseNumber: p.sepaHouseNumber,
    postalCode: p.sepaPostalCode,
    city: p.sepaCity,
    country: p.sepaCountry,
    iban: p.sepaIban,
    bic: p.sepaBic,
    accountHolder: p.sepaAccountHolder,
    signatureHeading: p.sepaSignatureHeading,
    signingPlace: p.sepaSigningPlace,
    signingDate: p.sepaSigningDate,
    confirmCheckbox: p.sepaConfirmCheckbox,
    typedNameLabel: p.sepaTypedNameLabel,
    typedNameHelper: p.sepaTypedNameHelper,
    submit: p.sepaSubmit,
    submitting: p.sepaSubmitting,
    cancel: p.cancel,
    genericError: p.sepaGenericError,
    ibanInvalid: p.sepaIbanInvalid,
    typedNameMismatch: p.sepaTypedNameMismatch,
    mandatoryField: p.sepaMandatoryField,
  };
}
